import { useApolloClient } from "@apollo/client";
import React from "react";
import type { ModifyItemProductValuesInput } from "../../../../clients/graphqlTypes.js";
import { useProductBox_ModifyItemProductMutation } from "../../doc-items/boxes/ProductBox.generated.js";
import type { ProductCreateModalProps } from "../../products/modals/ProductCreateModal.js";
import { productFormValuesToInput } from "../../doc-items/boxes/ProductBox.js";

interface Options {
  projectId: string | null;
  docId: string;
}

export type ModifyItemProductFn = (
  itemId: string,
  values: Partial<ModifyItemProductValuesInput>
) => Promise<void>;

export type CreateItemProductFn = (
  itemId: string,
  values: Parameters<ProductCreateModalProps["handleSubmit"]>[0]
) => Promise<void>;

export function useModifyItemProduct({ projectId, docId }: Options) {
  const client = useApolloClient();
  const [modifyItemProductMutation] = useProductBox_ModifyItemProductMutation({
    client,
  });

  const modifyItemProduct: ModifyItemProductFn = React.useCallback(
    async (itemId: string, values: Partial<ModifyItemProductValuesInput>) => {
      const result = await modifyItemProductMutation({
        variables: {
          input: {
            projectId,
            docId,
            itemId,
            values,
          },
        },
      });
      if (result.errors?.length) {
        throw new Error(result.errors[0].message);
      }
      if (!result.data) {
        throw new Error("Could not modify item product");
      }
    },
    [modifyItemProductMutation, projectId, docId]
  );

  const createItemProduct: CreateItemProductFn = React.useCallback(
    (itemId, values) =>
      modifyItemProduct(itemId, productFormValuesToInput(values)),
    [modifyItemProduct]
  );

  return { modifyItemProduct, createItemProduct };
}
